import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import BackgroundAccents from "../common/BackgroundAccents";

const POSTS_PER_PAGE = 6;

const Home = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(
      `https://parmazing.net/wp-json/wp/v2/posts?per_page=${POSTS_PER_PAGE}&orderby=date&order=desc&_embed`
    )
      .then((res) => res.json())
      .then((data) => {
        // Ensure only N latest posts and sorted by date just in case
        const sorted = data.sort((a, b) => new Date(b.date) - new Date(a.date));
        setPosts(sorted.slice(0, POSTS_PER_PAGE));
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching posts:", err);
        setLoading(false);
      });
  }, []);

  // Helpers to safely extract embedded data
  const getFeaturedImage = (post) =>
    post?._embedded?.["wp:featuredmedia"] &&
    post._embedded["wp:featuredmedia"][0] &&
    (post._embedded["wp:featuredmedia"][0].media_details?.sizes?.medium?.source_url ||
      post._embedded["wp:featuredmedia"][0].source_url ||
      null);

  const estimateReadTime = (post) => {
    const text = post?.content?.rendered?.replace(/<[^>]*>/g, "") || "";
    const words = text.trim().split(/\s+/).filter(Boolean).length;
    const mins = Math.max(1, Math.ceil(words / 200)); // ~200 wpm
    return `${mins} min${mins > 1 ? "s" : ""} read`;
  };

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-start bg-gray-100 overflow-hidden pt-16 sm:pt-20 md:pt-32 pb-16 sm:pb-20 md:pb-32">
      <BackgroundAccents />
      <br />

      {/* Hero */}
      <div className="relative z-10 flex flex-col md:flex-row items-center md:items-start justify-between max-w-6xl mx-auto px-4 sm:px-6 md:px-12 gap-10 md:gap-16">
        <div className="max-w-lg text-center md:text-left flex-1">
          <h1 className="text-2xl sm:text-4xl md:text-6xl font-extrabold text-gray-900 leading-tight">
            Simplifying Marketing, <span className="text-yellow-600">Amplifying Creativity.</span>
          </h1>
          <p className="text-gray-700 text-base sm:text-lg mt-4 max-w-prose mx-auto md:mx-0">
            Weaving imagination with expertise in every moment that matters.
          </p>

          <div className="flex flex-col sm:flex-row sm:flex-wrap items-center sm:items-start justify-center md:justify-start gap-4 mt-6">
            <Link
              to="/aboutme"
              className="w-36 px-4 py-2 sm:px-6 sm:py-3 bg-gray-900 text-white text-base sm:text-lg font-semibold rounded-md hover:bg-yellow-600 transition duration-300 text-center"
            >
              About Me
            </Link>
            <Link
              to="/blog"
              className="w-36 px-4 py-2 sm:px-6 sm:py-3 bg-gray-900 text-white text-base sm:text-lg font-semibold rounded-md hover:bg-yellow-600 transition duration-300 text-center"
            >
              Blog
            </Link>
          </div>
        </div>

        <div className="w-full md:w-1/2">
          <img
            src="https://parmazing.net/wp-content/uploads/img_1482.jpg"
            alt="About Me"
            className="w-full h-full object-cover rounded-2xl shadow-lg transform hover:scale-105 transition duration-300"
          />
        </div>
      </div>

      {/* Latest Blog Posts */}
      <section className="relative z-10 w-full max-w-6xl mx-auto mt-16 sm:mt-20 px-4 sm:px-6">
        <h2 className="text-xl sm:text-3xl md:text-4xl font-bold text-center text-gray-800">Latest Posts</h2>
        <div className="w-16 sm:w-20 h-1 bg-yellow-500 mx-auto mt-2 rounded-full" />

        {loading ? (
          <p className="text-center text-gray-600 mt-6">Loading latest posts...</p>
        ) : (
          <div className="mt-12 grid gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {posts.map((post) => {
              const featured = getFeaturedImage(post);
              const readTime = estimateReadTime(post);

              return (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="group block bg-white rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 border border-transparent hover:border-yellow-200 overflow-hidden"
                >
                  {/* Featured image */}
                  {featured ? (
                    <div className="w-full h-44 sm:h-48 md:h-40 lg:h-44 overflow-hidden">
                      <img
                        src={featured}
                        alt={post.title.rendered || "featured image"}
                        className="w-full h-full object-cover transform group-hover:scale-105 transition duration-300"
                      />
                    </div>
                  ) : (
                    <div className="w-full h-44 sm:h-48 md:h-40 lg:h-44 bg-gray-100 flex items-center justify-center text-gray-400">
                      No image
                    </div>
                  )}

                  {/* Card content */}
                  <div className="p-4 sm:p-5">
                    <h3 className="font-semibold text-lg text-gray-900 group-hover:text-yellow-700 transition-colors line-clamp-2 mb-2 leading-tight">
                      {/* title might include entities - render as plain text */}
                      <span dangerouslySetInnerHTML={{ __html: post.title.rendered }} />
                    </h3>

                    <p className="text-xs sm:text-sm text-gray-500 mb-3">
                      {new Date(post.date).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </p>

                    {post.excerpt && (
                      <div
                        className="text-sm text-gray-600 line-clamp-2 sm:line-clamp-3 mb-4"
                        dangerouslySetInnerHTML={{
                          __html: post.excerpt.rendered.replace(/<[^>]*>/g, ""),
                        }}
                      />
                    )}

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">

                        <div className="text-sm text-gray-700">
                          <div className="text-xs text-gray-500">{readTime}</div>
                        </div>
                      </div>

                      <div className="text-yellow-600 text-sm font-medium group-hover:text-yellow-700 transition-colors">
                        Read more →
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default Home;
